import routes from 'sitewide-js/core_pre_webpack/routes';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';

export const getMentionsCount = async (user_id: string) => {
  const url = routes.api_v0_profiles_mention_count_path({
    subdomain_param: 'api',
    user_id: user_id,
  });
  const response = await fetchWithCsrf(url);

  if (response.status !== 200) {
    throw new Error(`Unable to fetch mentions count`);
  }

  const data = await response.json();

  return parseInt(data.total_count, 10);
};

// Citations + Mentions counts for the profile owner, using the Citations
// Dashboard's category logic. Only succeeds for viewers in a dashboard variant
// (the endpoint 403s otherwise); callers gate on the server-rendered placeholder.
export const getCitationsDashboardCounts = async (user_id: string) => {
  const url = routes.api_v0_profiles_citations_dashboard_counts_path({
    subdomain_param: 'api',
    user_id: user_id,
  });
  const response = await fetchWithCsrf(url);

  if (response.status !== 200) {
    throw new Error(`Unable to fetch citations dashboard counts`);
  }

  const data = await response.json();

  return {
    citations: parseInt(data.citations, 10),
    mentions: parseInt(data.mentions, 10),
  };
};

// Which tier of citation metadata produced the descriptor. Server-side vocabulary, carried for
// analytics alone and read by both events the card sends: the impression counts the tiers that
// were on offer, and the click — every row shares one source — reports which tier was clicked.
// The descriptor already arrives as finished prose, so nothing here should compose copy from
// this; it is not a rendering hint.
export type CitingSourceKind = 'university' | 'journal' | 'country';

export type CitingSource = {
  descriptor: string;
  // Optional because the endpoint caches rows for 10 minutes: entries written before the tier
  // shipped carry no `kind` and are served until they expire. Those rows predate every tier
  // name, so counting them towards none of the tiers is correct rather than a fallback, and a
  // click on one reports no kind instead of a wrong one.
  kind?: CitingSourceKind;
  citation_id: number;
  // The citing work's author photo — an absolute S3 or gravatar URL, already sized by the
  // server. Null, commonly, when the citation reaches no photo-bearing Academia author; the
  // card supplies the site placeholder instead.
  photo_url: string | null;
};

export type CitingSources = {
  rows: CitingSource[];
  // The server's verdict on which photo derivative it served, not a client premium check, so
  // the two can never disagree about whether to blur.
  photosBlurred: boolean;
};

// A factory, not a shared constant: this value is returned straight to a caller, and one
// consumer's `rows.push` must not surface in the next one's empty state. Exported so the card's
// fetch-failure path uses this same "absent flag degrades to blurred" default rather than
// restating it.
export const emptyCitingSources = (): CitingSources => ({
  rows: [],
  photosBlurred: true,
});

// Owner-only: 403s for other users and for off-arm viewers. Empty is normal.
export const getCitingSources = async (
  user_id: string,
): Promise<CitingSources> => {
  const url = routes.api_v0_profiles_citing_sources_path({
    subdomain_param: 'api',
    user_id: user_id,
  });
  const response = await fetchWithCsrf(url);

  if (response.status !== 200) {
    throw new Error(`Unable to fetch citing sources`);
  }

  const data = await response.json();

  if (!Array.isArray(data.rows)) return emptyCitingSources();

  // `!== false`: an absent flag degrades to blurred.
  return { rows: data.rows, photosBlurred: data.photos_blurred !== false };
};
